import React, { useEffect, useMemo, useState } from 'react';
import { MagnifyingGlass, ArrowsDownUp } from '@phosphor-icons/react';
import { api } from '../api';
import { todayISO, canAddVehicle, stageProgress, KEYS_DONE, BRAND_OPTIONS, timeToMinutes } from '../helpers';
import DashStrip from '../components/DashStrip';
import FlowRow from '../components/FlowRow';
import VehicleCard from '../components/VehicleCard';

// Reactive mobile check — flips live when the window crosses 900px.
// The handoff's row is a desktop layout; phones keep the purpose-built card.
function useIsMobile() {
  const [mobile, setMobile] = useState(() => window.matchMedia('(max-width: 900px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)');
    const fn = (e) => setMobile(e.matches);
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);
  return mobile;
}

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

// Monday of the current week + offsetWeeks, at local midnight.
function mondayOf(offsetWeeks) {
  const d = new Date();
  const day = d.getDay();              // 0=Sun .. 6=Sat
  const toMon = day === 0 ? 6 : day - 1;
  d.setDate(d.getDate() - toMon + offsetWeeks * 7);
  d.setHours(0, 0, 0, 0);
  return d;
}
function isoOf(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
// "14 – 20 JUL" or "28 JUL – 3 AUG" (spanning months)
function weekLabel(monday) {
  const sun = new Date(monday); sun.setDate(sun.getDate() + 6);
  const m1 = MONTHS[monday.getMonth()].toUpperCase();
  const m2 = MONTHS[sun.getMonth()].toUpperCase();
  return monday.getMonth() === sun.getMonth()
    ? `${monday.getDate()} – ${sun.getDate()} ${m1}`
    : `${monday.getDate()} ${m1} – ${sun.getDate()} ${m2}`;
}

// Order inside a day group: deliveries (SOLD) first, then demos, then the rest
const DEAL_ORDER = { SOLD: 0, DEMO: 1, CUSTOMER: 2, STOCK: 3, USED: 4 };
const dealRank  = (v) => DEAL_ORDER[v.deal_status] ?? 9;
const brandRank = (v) => { const i = BRAND_OPTIONS.indexOf(v.brand); return i < 0 ? 99 : i; };
const timeRank  = (v) => timeToMinutes(v.delivery_time);

// Sorting always happens inside a day — the day grouping is the backbone of
// the list. Each option falls back to the other two so the order is stable.
export const SORTS = {
  deal:  { label: 'Deal type', by: (a, b) => dealRank(a) - dealRank(b)   || timeRank(a) - timeRank(b)  || brandRank(a) - brandRank(b) },
  brand: { label: 'Brand',     by: (a, b) => brandRank(a) - brandRank(b) || timeRank(a) - timeRank(b)  || dealRank(a) - dealRank(b) },
  time:  { label: 'Time',      by: (a, b) => timeRank(a) - timeRank(b)   || dealRank(a) - dealRank(b)  || brandRank(a) - brandRank(b) },
};

// Group vehicles by delivery day → [{ dateISO, label, isToday, rows }] sorted
// by date asc; vehicles without a date go last under "No delivery date".
function groupByDay(list, sortKey) {
  const today = todayISO();
  const cmp = (SORTS[sortKey] || SORTS.deal).by;
  const map = new Map();
  list.forEach((v) => {
    const d = String(v.delivery_date || '').slice(0, 10) || 'none';
    if (!map.has(d)) map.set(d, []);
    map.get(d).push(v);
  });
  const keys = [...map.keys()].sort((a, b) =>
    a === 'none' ? 1 : b === 'none' ? -1 : a.localeCompare(b));
  return keys.map((d) => {
    const rows = map.get(d).sort((a, b) => cmp(a, b) || String(a.model).localeCompare(String(b.model)));
    if (d === 'none') return { dateISO: d, label: 'NO DELIVERY DATE', isToday: false, rows };
    const [y, m, day] = d.split('-').map(Number);
    const dt = new Date(y, m - 1, day);
    return {
      dateISO: d,
      label: `${DAYS[dt.getDay()]} ${day} ${MONTHS[m - 1].toUpperCase()}`,
      isToday: d === today,
      rows,
    };
  });
}

export default function Dashboard({ session, refreshKey, onUpdate, onAdd, onRefresh }) {
  const role = session?.role;
  const isMobile = useIsMobile();
  const [vehicles, setVehicles] = useState([]);
  const [stats, setStats]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [tab, setTab]           = useState('active');   // 'active' | 'delivered'
  // Remembered, or the sort resets to default every time you leave the page
  const [sort, setSort] = useState(() => localStorage.getItem('vf_sort') || 'deal');
  const [weekOffset, setWeekOffset] = useState(0);
  const [day, setDay]           = useState(null);       // ISO date, or null = whole week

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([api.listVehicles(), api.dashboardStats()])
      .then(([v, s]) => { if (!cancelled) { setVehicles(v); setStats(s); } })
      .catch((e) => console.error(e))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refreshKey]);

  const monday = useMemo(() => mondayOf(weekOffset), [weekOffset]);
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday); d.setDate(d.getDate() + i);
    return { iso: isoOf(d), wd: DAYS[d.getDay()], n: d.getDate() };
  }), [monday]);
  const weekStartISO = weekDays[0].iso;
  const weekEndISO   = weekDays[6].iso;

  // Changing week clears a day pick that no longer exists in it
  useEffect(() => {
    if (day && (day < weekStartISO || day > weekEndISO)) setDay(null);
  }, [day, weekStartISO, weekEndISO]);

  const filtered = useMemo(() => {
    // Handed over = every applicable stage complete AND the keys are in the car.
    // Keys alone isn't enough — otherwise moving the keys mid-workflow would
    // yank a car that still has work on it out of the active list.
    let list = vehicles.filter((v) => {
      const gone = v.keys_status === KEYS_DONE && stageProgress(v) === 100;
      return tab === 'delivered' ? gone : !gone;
    });
    // Searching is a lookup across everything — if you type a stock number you
    // want the car wherever it is, not "not in this week".
    if (search) {
      const q = search.toLowerCase();
      return list.filter((v) =>
        (v.stock_number || '').toLowerCase().includes(q) ||
        (v.model || '').toLowerCase().includes(q)
      );
    }
    return list.filter((v) => {
      const d = String(v.delivery_date || '').slice(0, 10);
      // A car with no date belongs to no week, so it used to be invisible
      // everywhere. Show it until someone gives it a date.
      if (!d) return !day;
      return day ? d === day : d >= weekStartISO && d <= weekEndISO;
    });
  }, [vehicles, tab, search, day, weekStartISO, weekEndISO]);

  if (loading) return <div className="loading">Loading dashboard…</div>;

  const groups = groupByDay(filtered, sort);

  return (
    <div>
      <DashStrip
        stats={stats}
        canAdd={canAddVehicle(role)}
        onAdd={onAdd}
        onSearchRisk={() => { setTab('active'); setWeekOffset(0); setDay(todayISO()); setSearch(''); }}
      />

      {/* Header band: tabs · week nav · search · day chips */}
      <div className="fband">
        <div className="fband-tabs">
          <button className={`fband-tab ${tab === 'active' ? 'on' : ''}`} onClick={() => setTab('active')}>Vehicle Flow</button>
          <button className={`fband-tab ${tab === 'delivered' ? 'on' : ''}`} onClick={() => setTab('delivered')}>Delivered</button>
        </div>

        <div className="fband-nav">
          <button onClick={() => setWeekOffset((w) => w - 1)} title="Previous week">‹</button>
          <button className="fband-range" onClick={() => { setWeekOffset(0); setDay(null); }} title="Back to this week">
            {weekLabel(monday)}
          </button>
          <button onClick={() => setWeekOffset((w) => w + 1)} title="Next week">›</button>
        </div>

        <div className="fband-search">
          <MagnifyingGlass size={16} weight="bold" color="var(--muted)" />
          <input
            placeholder="Search stock or model"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <label className="fband-sort" title="Order inside each day">
          <ArrowsDownUp size={14} weight="bold" />
          <select
            value={sort}
            onChange={(e) => { setSort(e.target.value); localStorage.setItem('vf_sort', e.target.value); }}
          >
            {Object.entries(SORTS).map(([k, s]) => (
              <option key={k} value={k}>{s.label}</option>
            ))}
          </select>
        </label>

        <div className="fband-days">
          {weekDays.slice(0, 6).map((d) => (
            <div
              key={d.iso}
              className={`fday ${day === d.iso ? 'on' : ''}`}
              onClick={() => setDay((cur) => (cur === d.iso ? null : d.iso))}
              title={day === d.iso ? 'Click to show the whole week' : 'Show this day only'}
            >
              <div className="fday-wd">{d.wd}</div>
              <div className="fday-n num">{d.n}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Vehicle rows — handoff row on desktop, the card on phones */}
      <div className={isMobile ? 'vcard-list' : 'frows'}>
        {filtered.length === 0 ? (
          <div className="frow-empty">
            {tab === 'delivered' ? 'No vehicles delivered in this week.' : 'No vehicles scheduled for this week.'}
          </div>
        ) : (
          groups.map((g) => (
            <React.Fragment key={g.dateISO}>
              {/* One day picked → the chip already says which; skip the separator */}
              {!day && (
                <div className={`fsep ${g.isToday ? 'today' : ''} ${g.dateISO === 'none' ? 'nodate' : ''}`}>
                  {g.isToday ? 'TODAY · ' : ''}{g.label}
                  <span className="fsep-count">
                    {g.rows.length} car{g.rows.length > 1 ? 's' : ''}
                    {g.dateISO === 'none' ? ' · set a date so they show in a week' : ''}
                  </span>
                </div>
              )}
              {g.rows.map((v) => (
                isMobile
                  ? <VehicleCard key={v.id} vehicle={v} onUpdate={onUpdate} onChanged={onRefresh} role={role} />
                  : <FlowRow    key={v.id} vehicle={v} onUpdate={onUpdate} onChanged={onRefresh} role={role} />
              ))}
            </React.Fragment>
          ))
        )}
      </div>
    </div>
  );
}
